<script setup lang="ts">
import { signOut } from 'firebase/auth'
import { auth } from '@/firebase'
import { watch } from 'vue'
import { pwaConfig } from '@/utils/pwaTools'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'

const domain = import.meta.env.VITE_DOMAIN

const authStore = useAuthStore()
const { isAuthenticated } = storeToRefs(authStore)

const handleSignOut = async () => {
  try {
    await signOut(auth)
    // Redirect to login page
    window.location.href = '/login'
  } catch (error) {
    console.error('Error signing out:', error)
  }
}

watch(
  isAuthenticated,
  (newVal) => {
    if (newVal) {
      handleSignOut()
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-base-100 flex items-center justify-center p-4">
    <div class="max-w-2xl w-full">
      <div class="bg-white rounded-3xl shadow-xl p-8 md:p-12 text-center">
        <!-- Title -->
        <h1 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">App Discontinued</h1>

        <!-- Description -->
        <div class="space-y-4 text-gray-600 mb-8">
          <p class="text-base leading-relaxed">
            To continue using Greatly Health, please access the application through your web browser
            instead of the installed app.
          </p>
        </div>

        <!-- Instructions -->
        <div class="bg-base-200 rounded-2xl p-6 mb-8 text-left">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">How to access the web app:</h2>
          <ol class="space-y-3 text-gray-700">
            <li class="flex items-start gap-3">
              <span class="font-semibold text-gray-900 flex-shrink-0">1.</span>
              <span>Uninstall this app from your device</span>
            </li>
            <li class="flex items-start gap-3">
              <span class="font-semibold text-gray-900 flex-shrink-0">2.</span>
              <span>Open your web browser (Chrome, Safari, Firefox, etc.)</span>
            </li>
            <li class="flex items-start gap-3">
              <span class="font-semibold text-gray-900 flex-shrink-0">3.</span>
              <span
                >Navigate to
                <a :href="`https://${domain}`" target="_blank" class="text-blue-500"
                  >https://{{ domain }}</a
                ></span
              >
            </li>
            <li class="flex items-start gap-3">
              <span class="font-semibold text-gray-900 flex-shrink-0">4.</span>
              <span>Sign in with your account credentials</span>
            </li>
          </ol>
        </div>

        <!-- Device-specific instructions -->
        <div class="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8 text-left">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Uninstall Instructions:</h3>
          <div class="space-y-4">
            <div v-if="pwaConfig.other.isAndroidChrome">
              <p class="font-semibold text-gray-900 mb-2">On Android:</p>
              <p class="text-sm text-gray-700">
                Go to Settings → Apps → Greatly Health → Uninstall
              </p>
            </div>
            <div v-if="pwaConfig.other.isIOSBrowserSafari">
              <p class="font-semibold text-gray-900 mb-2">On iOS:</p>
              <p class="text-sm text-gray-700">
                Long-press the app icon and select "Remove App" → "Delete App"
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Additional styles if needed */
</style>
